// Helpers
import {
  parseISO,
  add,
  differenceInDays,
  startOfToday,
  format,
  DATE_TIME_FORMATS,
  normaliseDateToString,
} from '@white-label-helper/date-utilities';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getRegionVariable } from '@white-label-helper/get-region-variable';
import type { LoungesSearchCriteria } from '@white-label-types/product-types';
import type { CartItem } from '@white-label-types/parking-checkout';
import type { SearchProducts, PurchaseProduct } from '@white-label-types/parking-booking';
import { LoungeFormData } from '@white-label-types/search-box';
import type { TravelInsuranceForm } from '@white-label-store/travel-insurance';

type GaDataLayer = {
  /** Whether the user is logged in or not, we should be able to get this from auth0 this.$auth.isAuthenticated */
  loginStatus: boolean;
  /** The user's ID. There isn't a clear ID with auth0 but the user.sub is the closest atm this.$auth.user.sub */
  userId: string | undefined;
  /** The airport name */
  airportName: string | undefined;
  /** The airport code */
  airportCode: string | undefined;
  /** The airport terminal number or "dontKnow" */
  airportTerminalId?: string;
  /** What the service is (e.g. "parking") */
  serviceType: string;
  /** How far in advance the booking was made */
  daysAheadOfBooking: number | undefined;
  /** The date of departure */
  parkingDepartureDate?: string;
  /** The time of departure */
  parkingDepartureTime?: string;
  /** The date of return */
  parkingReturnDate?: string;
  /** The time of return */
  parkingReturnTime?: string;
  /** The duration between departure and return */
  stayDuration?: number;
  /** The order reference */
  orderReference?: string;
  /** The coupon or voucher code */
  couponVoucher?: string;
  /** The name of the partner */
  partnerName: string | undefined;
  /** The language of the site */
  siteLanguage: string;
  /** The parking name */
  carParkName?: string;
  /** The Lounge Departure date */
  loungeDepartureDate?: string;
  /** The Lounge Departure time */
  loungeDepartureTime?: string;
  /** The Lounge Return date */
  loungeReturnDate?: string;
  /** The Lounge Return time */
  loungeReturnTime?: string;
  /** The FastTrack Departure date */
  fastTrackDepartureDate?: string;
  /** The FastTrack Departure time */
  fastTrackDepartureTime?: string;
  /** The FastTrack Return date */
  fastTrackReturnDate?: string;
  /** The FastTrack Return time */
  fastTrackReturnTime?: string;
  /** The Airline code */
  airLine: string | undefined;
  /** Terminal ID */
  terminalId: string | undefined;
  /** The customers chosen departure date */
  flightDepartureDate: string | undefined;
  /** The customers chosen flight departure time */
  flightDepartureTime: string | undefined;
  /** Baggage type */
  baggage?: string;
  /** Requested for tracking but currently not sure what to use */
  userType: string | undefined;
  /** Lounge Name, attached if the product has one */
  loungeName?: string | undefined;
  /** FastTrack Name, attached if the product has one */
  fastTrackName?: string | undefined;
  /** FastTrack journey type, can be 'oneWay' or 'twoWay' */
  journeyType?: string | undefined;
  /** Lounge entry date */
  entryDate: string | undefined;
  /** Lounge entry time */
  entryTime: string | undefined;
  /** Number of guests */
  numberOfGuest: number | undefined;
  /** Number of adults and seniors */
  adult?: number;
  /** Number of Children and Infants */
  children?: number;
  /** GA event name */
  event?: string;
  /** The users selected timeslot */
  timeslot: string | undefined;
  /** Region */
  region: string;
  /** Region */
  country: string | undefined;
  /** Travel Insurance Trip Start Date */
  tripStartDate?: string;
  /** Travel Insurance Trip End Date */
  tripEndDate?: string;
  /** Travel Insurance Trip Duration */
  tripDuration?: number;
  /** Travel Insurance number of all travellers */
  numberOfTraveller?: number;
  /** Travel Insurance selected addons */
  InsuranceAddOn?: string;
};

/** The data object to send to GA */
const gaLayer: GaDataLayer = {
  loginStatus: false,
  userId: undefined,
  airportName: undefined,
  airportCode: undefined,
  airportTerminalId: undefined,
  serviceType: 'parking',
  daysAheadOfBooking: 0,
  parkingDepartureDate: undefined,
  parkingDepartureTime: undefined,
  parkingReturnDate: undefined,
  parkingReturnTime: undefined,
  stayDuration: 0,
  couponVoucher: undefined,
  partnerName: undefined,
  siteLanguage: 'en-US',
  carParkName: undefined,
  event: 'globalParameters',
};

type Options = {
  loginStatus?: boolean;
  userId: number | undefined;
  airportName: string | undefined;
  airportCode: string | undefined;
  airportTerminalId: string | undefined;
  serviceType?: string;
  daysAheadOfBooking?: number;
  parkingDepartureDate: string | undefined;
  parkingDepartureTime: string | undefined;
  parkingReturnDate: string | undefined;
  parkingReturnTime?: string;
  stayDuration?: number;
  orderReference: string | undefined;
  couponVoucher: string | undefined;
  partnerName: string | undefined;
  siteLanguage?: string;
  carParkName: string | undefined;
  event?: string;
  groups?: { [key: string]: number | undefined } | null | undefined;
};

/**
 * Update the `gaLayer` object with new options.
 * Persists existing values if a new value is not provided.
 * Intended to be used with `gtm.push` to update the "All Pages DataLayer" in GTM.
 * @param options - The options to update the dataLayer with - can be a subset of possible options
 * @returns The updated dataLayer object
 */

export const gaDataLayer = (options: Partial<Options>) => {
  const {
    loginStatus = gaLayer.loginStatus,
    airportName = gaLayer.airportName,
    airportCode = gaLayer.airportCode,
    airportTerminalId = gaLayer.airportTerminalId,
    parkingDepartureDate = gaLayer.parkingDepartureDate,
    parkingDepartureTime = gaLayer.parkingDepartureTime,
    parkingReturnDate = gaLayer.parkingReturnDate,
    parkingReturnTime = gaLayer.parkingReturnTime,
    couponVoucher = gaLayer.couponVoucher,
    partnerName = gaLayer.partnerName,
    siteLanguage = gaLayer.siteLanguage,
    carParkName = gaLayer.carParkName,
    ...remainingOptions
  } = options;

  if (parkingDepartureDate) {
    gaLayer.daysAheadOfBooking = differenceInDays(
      parseISO(parkingDepartureDate),
      startOfToday()
    );
  }

  if (parkingDepartureDate && parkingReturnDate) {
    const arrival = parseISO(parkingReturnDate);
    const departure = parseISO(parkingDepartureDate);
    const addOneDay = add(arrival, { days: 1 });
    // Adding 1 day to arrival to make sure it's included in the calculation
    const days = (+addOneDay - +departure) / 864e5; // Days in milliseconds
    // The min value is 1
    gaLayer.stayDuration = Math.round(days) || 1;
  }

  if (options.event) {
    gaLayer.event = options.event;
  }

  if (options.orderReference) {
    gaLayer.orderReference = options.orderReference;
  }

  if (options.groups) {
    const numberOfGuest = Object.values(options.groups)
      .filter<number>((value): value is number => typeof value === 'number')
      .reduce((acc, value) => acc + value, 0) || undefined;
    const adult = (options.groups['adult'] || 0) + (options.groups['senior'] || 0) || undefined;
    const children = (options.groups['child'] || 0) + (options.groups['infant'] || 0) || undefined;

    gaLayer.numberOfGuest = numberOfGuest;
    gaLayer.adult = adult;
    gaLayer.children = children;
  }

  const country = getAppVariable('pois')?.find(poi => poi.code === airportCode)?.country?.name;

  gaLayer.loginStatus = loginStatus;
  gaLayer.airportCode = airportCode;
  gaLayer.airportTerminalId = airportTerminalId;
  gaLayer.parkingDepartureDate = parkingDepartureDate;
  gaLayer.parkingDepartureTime = parkingDepartureTime;
  gaLayer.parkingReturnDate = parkingReturnDate;
  gaLayer.parkingReturnTime = parkingReturnTime;
  gaLayer.couponVoucher = couponVoucher;
  gaLayer.partnerName = partnerName;
  gaLayer.siteLanguage = siteLanguage;
  gaLayer.carParkName = carParkName;
  gaLayer.airportName = airportName;
  gaLayer.entryDate = parkingDepartureDate;
  gaLayer.entryTime = parkingDepartureTime;
  gaLayer.region = getRegionVariable('code');
  gaLayer.country = country;

  Object.assign(gaLayer, remainingOptions);

  return gaLayer;
};

/**
 *finds journey type based on departure and return dates
 */
const storedDirections: string[] = []; // To help determine flying In/Out when evaluating multiple products in a basket.

function hasMixedDirections(): boolean {
  if (storedDirections.length <= 1) return false;
  const firstDirection = storedDirections[0];
  return storedDirections.some(direction => direction !== firstDirection);
}

export function determineJourneyType(departureDate?: string, returnDate?: string, direction?: string): string | undefined {
  if (direction) {
    if (direction.endsWith('-remove')) {
      const directionToRemove = direction.slice(0, -'-remove'.length);
      const existingIndex = storedDirections.indexOf(directionToRemove);
      if (existingIndex !== -1) {
        storedDirections.splice(existingIndex, 1);
      }
    } else {
      storedDirections.push(direction);
    }

    if (hasMixedDirections()) return 'flyingInAndOut';
    if (storedDirections.includes('inbound')) return 'flyingIn';
    if (storedDirections.includes('outbound')) return 'flyingOut';
  }

  if (departureDate && returnDate) return 'flyingInAndOut';
  if (departureDate) return 'flyingOut';
  if (returnDate) return 'flyingIn';
  return undefined;
}

/**
 * maps product codes to their GTM key prefixes
 */
export function getProductKeyPrefix(productCode: string): string {
  const mapping: Record<string, string> = {
    'fasttrack': 'fastTrack',
    'lounges': 'lounge',
    'parking': 'parking',
  };
  return mapping[productCode] || productCode;
}

/**
 * populates product dates from CartItem or SearchProducts
 */
export function createProductDateKeys(
  item: CartItem | SearchProducts,
  productCode: string
): Partial<GaDataLayer> {
  const formattedDates = getFormattedProductDates(item);
  if (!formattedDates.date) {
    return {};
  }

  const keyName: string =  getProductKeyPrefix(productCode)

  return {
    [`${keyName}DepartureDate`]: formattedDates.date,
    [`${keyName}DepartureTime`]: formattedDates.time,
    [`${keyName}ReturnDate`]: formattedDates.date2,
    [`${keyName}ReturnTime`]: formattedDates.time2,
  };
}

/**
 * extracts entry and exit dates from a product object and formats them into standardized strings.
 * made for consistentiency of date and time formatting for GTM tracking.
 * @param item - product item that can be from cart, search results or a purchase.
 * @returns object containing formatted date and time strings
 */

export function getFormattedProductDates(item: CartItem | SearchProducts | PurchaseProduct): { date?: string; time?: string; date2?: string; time2?: string } {
  const itemData = extractProductData(item);

  if (!itemData.entryDate) return {};

  const dates: { date?: string; time?: string; date2?: string; time2?: string } = {
    date: format(new Date(itemData.entryDate), DATE_TIME_FORMATS.year_month_day),
    time: format(new Date(itemData.entryDate), DATE_TIME_FORMATS.time_24hr),
    date2: undefined,
    time2: undefined,
  };

  if (itemData.exitDate) {
    dates.date2 = format(new Date(itemData.exitDate), DATE_TIME_FORMATS.year_month_day);
    dates.time2 = format(new Date(itemData.exitDate), DATE_TIME_FORMATS.time_24hr);
  }
  return dates;
}

/**
 * takes whatever date and time exists in the form data and converts it all to the same format
 * made to handle inconsistencies in property names (like 'date', 'date1', 'entryDate').
 * @param formData - form data object from a lounge or fasttrack search.
 * @returns object with a consistent structure for departure and return dates and times.
 */
export function normaliseProductDates(formData: LoungesSearchCriteria['lounges']): {
  departureDate?: string;
  departureTime?: string;
  returnDate?: string;
  returnTime?: string;
} {
  return {
    departureDate: formData?.date || formData?.date1 || formData.entryDate || undefined,
    departureTime: formData?.time || formData?.time1 || formData.entryTime?.value || undefined,
    returnDate: formData?.date2 || undefined,
    returnTime: formData?.time2 || undefined,
  };
}

type ProductExtendOptions = Partial<Pick<GaDataLayer, 'loungeName' | 'fastTrackName'>>;

/**
 * creates a GTM dataLayer object for non-parking products (lounges, fasttrack).
 * it normalises date/time inputs and maps them to the correct GTM properties based on the product code.
 * @param productCode - code of the product ('lounges' or 'fasttrack').
 * @param formData - search form data related to the product.
 * @param extend - optional object to provide additional details like loungeName or fastTrackName.
 * @returns partial GTM dataLayer object with product-specific fields.
 */
export function getProductGaData(
  productCode: string,
  formData: LoungesSearchCriteria['lounges'],
  extend: ProductExtendOptions = {}
): Partial<GaDataLayer> {
  const data: Partial<GaDataLayer> = {};
  const dates = normaliseProductDates(formData);
  if (productCode === 'fasttrack') {
    data.fastTrackDepartureDate = dates.departureDate;
    data.fastTrackDepartureTime = dates.departureTime;
    data.fastTrackReturnDate = dates.returnDate;
    data.fastTrackReturnTime = dates.returnTime;
    data.fastTrackName = extend['fastTrackName'] || undefined;
    data.journeyType = determineJourneyType(dates.departureDate, dates.returnDate, extend['direction']);
  } else if (productCode === 'lounges') {
    data.loungeDepartureDate = dates.departureDate;
    data.loungeDepartureTime = dates.departureTime;
    data.loungeReturnDate = dates.returnDate;
    data.loungeReturnTime = dates.returnTime;
    data.loungeName = extend['loungeName'] || undefined;
    data.journeyType = determineJourneyType(dates.departureDate, dates.returnDate, extend['direction']);
  }
  return data;
}

/**
 * extracts product data from CartItem or SearchProducts
 * creates unified interface for GTM tracking
 */
export function extractProductData(item: CartItem | SearchProducts | PurchaseProduct) {
  if ('order_criteria' in item) {
    const purchaseProduct = item as PurchaseProduct;
    return {
      productCode: purchaseProduct.product_code,
      name: purchaseProduct.inventory_item?.product_name || purchaseProduct.inventory_item?.name || purchaseProduct.parkingName,
      entryDate: purchaseProduct.inventory_item?.entry_date,
      exitDate: purchaseProduct.inventory_item?.exit_date,
      searchCriteria: null,
      direction: purchaseProduct.inventory_item.direction || null,
    };
  }

  if ('search_criteria' in item) {
    const cartItem = item as CartItem;
    return {
      productCode: cartItem.product_code,
      name: cartItem.inventory_item?.name,
      entryDate: cartItem.inventory_item?.entry_date,
      exitDate: cartItem.inventory_item?.exit_date,
      searchCriteria: cartItem.search_criteria?.[cartItem.product_code],
      direction: cartItem.inventory_item.direction || null,
    };
  }

  const searchProduct = item as SearchProducts;
  return {
    productCode: searchProduct.productHeader.product_code,
    name: searchProduct.name,
    entryDate: searchProduct.entry_date,
    exitDate: searchProduct.exit_date,
    searchCriteria: null,
  };
}

/**
 * constructs GTM data object with fields specific to a given product (parking, lounge, fasttrack).
 * extracts and formats data from a cart or a purchased item.
 * @param item - cart item or purchased product object.
 * @returns partial GTM dataLayer object containing data specific to the product type.
 */
export function buildProductSpecificGtmData(
  item: CartItem | PurchaseProduct
): Partial<GaDataLayer> {
  const gtmData: Partial<GaDataLayer> = {};
  const itemData = extractProductData(item);
  const productCode = itemData.productCode;

  const correctedDates = getFormattedProductDates(item);
  let correctedCriteria = {};

  if ('order_criteria' in item) {
    const purchaseProduct = item as PurchaseProduct;
    correctedCriteria = {
      ...(purchaseProduct.order_criteria[
        productCode as keyof typeof purchaseProduct.order_criteria
      ] || {}),
      ...correctedDates,
    };
  } else if ('search_criteria' in item) {
    const cartItem = item as CartItem;
    correctedCriteria = {
      ...(cartItem.search_criteria?.[productCode] || {}),
      ...correctedDates,
    };
  }

  if (productCode === 'parking') {
    Object.assign(gtmData, {
      parkingDepartureDate: correctedDates.date,
      parkingDepartureTime: correctedDates.time,
      parkingReturnDate: correctedDates.date2,
      parkingReturnTime: correctedDates.time2,
      carParkName: itemData.name,
      couponVoucher: (item as PurchaseProduct).discount?.code || (item as CartItem).search_criteria?.parking?.coupon_code,
    });
  } else if (productCode === 'lounges' || productCode === 'fasttrack') {
    const productGtmData = getProductGaData(productCode, correctedCriteria, {
      loungeName: productCode === 'lounges' ? itemData.name : undefined,
      fastTrackName: productCode === 'fasttrack' ? itemData.name : undefined,
      direction: itemData.direction || undefined,
    });
    Object.assign(gtmData, productGtmData);
  } else if (productCode === 'travel_insurance') {
    const insuranceGtmData = buildInsuranceGtmData(item as CartItem);
    Object.assign(gtmData, insuranceGtmData);
  }

  return gtmData;
}

/**
 * builds GTM data for confirmed order items on the receipt page
 */
export function buildReceiptGtmData(
  items: PurchaseProduct[],
  poi: { code?: string; name?: string },
  authData: { isAuthenticated: boolean; userId?: string },
  orderReference: string
) {
  const finalGtmData: Partial<GaDataLayer> = {};

  const primaryItem = items.find(item => {
    const criteria = item.order_criteria[item.product_code as keyof typeof item.order_criteria];
    return !!(criteria && criteria.is_primary);
  }) || items.find(item => item.product_code === 'parking') || items[0];

  if (!primaryItem) {
    return {};
  }

  const primaryProductCode = primaryItem.product_code;

  //standalone insurance flow - skips gaDataLayerCommon
  if (primaryProductCode === 'travel_insurance') {
    const insuranceGtmData = buildInsuranceGtmData(primaryItem);
    const country = getAppVariable('pois')?.find(
      (p: { code: string; country?: { name?: string } }) =>
        p.code === (poi.code || getAppVariable('poi.code'))
    )?.country?.name;

    Object.assign(finalGtmData, {
      airportCode: poi.code || getAppVariable('poi.code'),
      airportName: poi.name || getAppVariable('poi.name'),
      partnerName: getAppVariable('partner_name'),
      siteLanguage: getAppVariable('default_language'),
      serviceType: primaryProductCode,
      region: getRegionVariable('code'),
      country,
      loginStatus: authData.isAuthenticated,
      userId: authData.userId,
      event: 'globalParameters',
      ...insuranceGtmData,
    });
  } else {
    const commonCriteria = primaryItem.order_criteria[primaryProductCode as keyof typeof primaryItem.order_criteria];
    if (commonCriteria) {
      const commonData = gaDataLayerCommon(commonCriteria, {
        loginStatus: authData.isAuthenticated,
        userId: authData.userId,
        couponVoucher: primaryItem.discount?.code,
        airportCode: poi.code,
        airportName: poi.name,
      });
      Object.assign(finalGtmData, commonData);
    }
  }

  finalGtmData.orderReference = orderReference;

  items.forEach(item => {
    const productGtmData = buildProductSpecificGtmData(item);
    Object.assign(finalGtmData, productGtmData);
  });

  return finalGtmData;
}

/**
 * builds GTM data for cart items on payment page
 */
export function buildCartItemsGtmData(
  cartItems: CartItem[],
  primaryProductType: string,
  poi: { code?: string; name?: string },
  authData: { isAuthenticated: boolean; userId?: string }
) {
  const primaryItem = cartItems.find(item => item.product_code === primaryProductType) || cartItems[0];

  if (!primaryItem) return {};

  //for standalone insurance flow - skips gaDataLayerCommon to avoid adding parking fields
  if (primaryProductType === 'travel_insurance' && primaryItem.product_code === 'travel_insurance') {
    const finalGtmData = buildStandaloneInsuranceCartGtmData(primaryItem, authData);

    //for safety to process other items in the cart
    cartItems.forEach(cartItem => {
      if (cartItem.product_code !== 'travel_insurance') {
        const productGtmData = buildProductSpecificGtmData(cartItem);
        Object.assign(finalGtmData, productGtmData);
      }
    });

    return finalGtmData;
  }

  //noninsurance flow - use existing gaDataLayerCommon path
  const finalGtmData: Partial<GaDataLayer> = {};
  const searchCriteria = primaryItem.search_criteria?.[primaryItem.product_code];
  if (searchCriteria) {
    const commonData = gaDataLayerCommon(searchCriteria, {
      loginStatus: authData.isAuthenticated,
      userId: authData.userId,
      couponVoucher: searchCriteria.coupon_code,
      airportCode: poi.code || getAppVariable('poi.code'),
      airportName: poi.name || getAppVariable('poi.name'),
    });
    Object.assign(finalGtmData, commonData);
  }

  cartItems.forEach(cartItem => {
    const productGtmData = buildProductSpecificGtmData(cartItem);
    Object.assign(finalGtmData, productGtmData);
  });

  return finalGtmData;
}

/**
 * builds GTM data for search products on travel extras page
 */
export function buildSearchProductGtmData(
  searchCriteria: LoungeFormData,
  product: SearchProducts,
  primaryProductType: string,
  insuranceFormData?: TravelInsuranceForm | null
) {
  const extraProductCode = product.productHeader.product_code;

  //gets common data
  const commonGtmData = gaDataLayerCommon(searchCriteria, {
    ...(searchCriteria.airport && { airportCode: searchCriteria.airport }),
  });

  //gets primary product data
  const primaryProductGtmData = getProductGaData(primaryProductType, searchCriteria);

  //handles travel insurance separately
  if (extraProductCode === 'travel_insurance') {
    const insuranceGtmData = buildInsuranceGtmData(product, insuranceFormData);
    return {
      ...commonGtmData,
      ...primaryProductGtmData,
      ...insuranceGtmData,
    };
  }

  //gets extra product dates (non-insurance)
  const extraProductDateData = createProductDateKeys(product, extraProductCode);
  const extraProductGtmData = {
    ...extraProductDateData,
    ...(extraProductCode === 'lounges' && { loungeName: product.name }),
    ...(extraProductCode === 'fasttrack' && { fastTrackName: product.name }),
  };

  return {
    ...commonGtmData,
    ...primaryProductGtmData,
    ...extraProductGtmData,
  };
}


/**
 * builds a base GTM dataLayer object with common properties from search form data.
 * it calculates guest counts, dates, airport info, and other shared details.
 * @param formData - search criteria or form data.
 * @param extend - object with additional properties to merge into the final datALayer.
 * @returns partial GTM dataLayer object containing common tracking parameters.
 */
export function gaDataLayerCommon(
  formData: LoungesSearchCriteria['lounges'],
  extend = {},
) {
  const groups = formData.customers || formData.groups || {};
  const numberOfGuest = Object.values(groups)
    .filter<number>((value): value is number => typeof value === 'number')
    .reduce((acc, value) => acc + value, 0);

  const primaryProductType = getAppVariable('primary_product_type');
  const entryDate = formData.entryDate || formData.date1 || formData.date || '';

  const data: Partial<GaDataLayer> = {
    airportCode: getAppVariable('poi.code'),
    terminalId: `${formData?.terminal || 'dontKnow'}`,
    airLine: formData?.airline || undefined,
    baggage: formData?.baggage_type || undefined,
    userType: undefined,
    partnerName: getAppVariable('partner_name'),
    daysAheadOfBooking: entryDate ? differenceInDays(parseISO(entryDate), startOfToday()) : undefined,
    entryDate: entryDate
      ? format(parseISO(entryDate), DATE_TIME_FORMATS.year_month_day)
      : undefined,
    entryTime: formData.entryTime?.value || formData.time1 || formData.time || undefined,
    numberOfGuest,
    adult: (groups['adult'] || 0) + (groups['senior'] || 0) || undefined,
    children: (groups['child'] || 0) + (groups['infant'] || 0) || undefined,
    loginStatus: false,
    userId: undefined,
    siteLanguage: getAppVariable('default_language'),
    event: 'globalParameters',
    serviceType: primaryProductType,
    ...extend,
  };

  return data;
}


export function gaDataLayerLounges(
  formData: LoungesSearchCriteria['lounges'],
  extend = {},
  productCode?: string,
) {
  const groups = formData.groups || {};
  const numberOfGuest = Object.values(groups)
    .filter<number>((value): value is number => typeof value === 'number')
    .reduce((acc, value) => acc + value, 0);

  const productTypeForTracking = productCode || getAppVariable('primary_product_type');
  const entryDate = formData.date1 || formData.date || '';

  const data: Partial<GaDataLayer> = {
    airportCode: getAppVariable('poi.code'),
    terminalId: `${formData?.terminal || 'dontKnow'}`,
    airLine: formData?.airline || undefined,
    baggage: formData?.baggage_type || undefined,
    userType: undefined,
    partnerName: getAppVariable('partner_name'),
    daysAheadOfBooking: differenceInDays(parseISO(entryDate), startOfToday()),
    entryDate: entryDate
      ? format(parseISO(entryDate), DATE_TIME_FORMATS.year_month_day)
      : undefined,
    entryTime: formData.time1 || formData.time || undefined,
    numberOfGuest,
    adult: (groups['adult'] || 0) + (groups['senior'] || 0) || undefined,
    children: (groups['child'] || 0) + (groups['infant'] || 0) || undefined,
    loginStatus: false,
    userId: undefined,
    siteLanguage: getAppVariable('default_language'),
    event: 'globalParameters',
    serviceType: productTypeForTracking,
    ...extend,
  };

  if (productTypeForTracking === 'fasttrack') {
    data.fastTrackDepartureDate = formData?.date || formData?.date1 || formData.entryDate || undefined;
    data.fastTrackDepartureTime = formData?.time || formData?.time1 || formData.entryTime?.value || undefined;
    data.fastTrackReturnDate = formData?.date2 || formData.entryDate || undefined;
    data.fastTrackReturnTime = formData?.time2 || formData.entryTime?.value || undefined;
    data.fastTrackName = extend.fastTrackName || undefined;
    data.journeyType = determineJourneyType(data.fastTrackDepartureDate, data.fastTrackReturnDate, extend['direction']);
  } else {
    data.loungeDepartureDate = formData?.date || formData?.date1 || formData.entryDate || undefined;
    data.loungeDepartureTime = formData?.time || formData?.time1 || formData.entryTime?.value || undefined;
    data.loungeReturnDate = formData?.date2 || formData.entryDate || undefined;
    data.loungeReturnTime = formData?.time2 || formData.entryTime?.value || undefined;
    data.loungeName = extend.loungeName || undefined;
    data.journeyType = determineJourneyType(data.loungeDepartureDate, data.loungeReturnDate, extend['direction']);
  }

  // direction is only used to determine journeyType, so we can remove it from the final data object
  if(data['direction']) delete data['direction'];

  return data;
}

/**
 * Values that are used at all steps of the journey
 * @returns The dataLayer object
 */
export const gaDataLayerTimeslotCommonValues: Partial<GaDataLayer> = {
  airportCode: '',
  terminalId: undefined,
  airLine: undefined,
  userType: undefined,
  partnerName: '',
  serviceType: 'securityFastTrack',
  daysAheadOfBooking: undefined,
  numberOfGuest: undefined,
  loginStatus: false,
  userId: undefined,
};

/**
 * Values that are used only in step 1 of the journey
 * @returns The dataLayer object
 */
export const gaDataLayerTimeslotStep1Values: Partial<GaDataLayer> = {
  siteLanguage: '',
  timeslot: undefined,
  flightDepartureDate: undefined,
  flightDepartureTime: undefined,
};

/**
 * Values that are used only in step 2 of the journey
 * @returns The dataLayer object
 */
export const gaDataLayerTimeslotStep2Values: Partial<GaDataLayer> = {
  loungeName: undefined,
  entryDate: undefined,
  entryTime: undefined,
  adult: 0,
  children: 0,
};

export const gaDataLayerTimeslotStep1InitialValues: GaDataLayer = { ...gaDataLayerTimeslotCommonValues, ...gaDataLayerTimeslotStep1Values } as GaDataLayer;
export const gaDataLayerTimeslotStep2InitialValues: GaDataLayer = { ...gaDataLayerTimeslotCommonValues, ...gaDataLayerTimeslotStep2Values } as GaDataLayer;

/**
 * Create the data layer object in step 1 of the journey
 * @param formData Values that are subject to change at this stage of the journey
 * @returns The dataLayer object
 */
export function gaDataLayerTimeslotHome(
  formData: {
    terminal?: string;
    numberOfGuest?: number;
    flightDepartureDate: string | undefined;
    airline?: string;
    flightDepartureTime?: string;
  },
): GaDataLayer {
  const result = gaDataLayerTimeslotStep1InitialValues;

  if (formData.flightDepartureDate) {
    result.daysAheadOfBooking = differenceInDays(parseISO(formData.flightDepartureDate), startOfToday());
  }
  result.terminalId = formData.terminal ?? result.terminalId;
  result.numberOfGuest = formData.numberOfGuest ?? result.numberOfGuest;
  result.flightDepartureDate = formData.flightDepartureDate ?? result.flightDepartureDate;
  result.airLine = formData.airline ?? result.airLine;
  result.flightDepartureTime = formData.flightDepartureTime ?? result.flightDepartureTime;
  result.airportCode = getAppVariable('poi.code');
  result.partnerName = getAppVariable('partner_name');
  result.siteLanguage = getAppVariable('default_language');

  return result;
};

// For step 2
export function gaDataLayerTimeslot(
  formData: {
    terminal?: string;
    passengers?: number;
    departureDate: string | undefined;
    airline?: string;
    flightDepartureTime?: string;
  },
): GaDataLayer {
  const result = gaDataLayerTimeslotStep2InitialValues;

  if (formData.departureDate) {
    result.daysAheadOfBooking = differenceInDays(parseISO(formData.departureDate), startOfToday());
  }

  result.terminalId = formData.terminal ?? result.terminalId;
  result.numberOfGuest = formData.passengers ?? result.numberOfGuest;
  result.entryDate = formData.departureDate ?? result.entryDate;
  result.airLine = formData.airline ?? result.airLine;
  result.flightDepartureTime = formData.flightDepartureTime ?? result.flightDepartureTime;
  result.adult = formData.passengers ?? result.adult;
  result.airportCode = getAppVariable('poi.code');
  result.partnerName = getAppVariable('partner_name');

  return result;
}

// For travel insurance

/**
 * Extracts insurance add-on names from sub_options, excluding base_package
 * Only includes selected add-ons (where selected === true or quantity > 0)
 * @param subOptions - Array of sub_options from insurance product
 * @returns Comma-separated string of add-on names
 */
export function getInsuranceAddOnNames(
  subOptions: Array<{
    id: string;
    details?: { name?: string };
    selected?: boolean;
    totals?: {
      quantity?: number;
      total?: number
    };
  }>
): string {
  if (!subOptions || !Array.isArray(subOptions)) return '';

  return subOptions
    .filter(opt => {
      //excluding base_package
      if (opt.id === 'base_package') return false;

      return opt.selected === true ||
             (opt.totals?.quantity !== undefined && opt.totals.quantity > 0) ||
             (opt.totals?.total !== undefined && opt.totals.total > 0);
    })
    .map(opt => opt.details?.name || opt.id)
    .filter(Boolean)
    .join(', ');
}

/**
 * Calculates trip duration in days from start and end dates
 * @param startDate - Trip start date string (YYYY-MM-DD format)
 * @param endDate - Trip end date string (YYYY-MM-DD format)
 * @returns Number of days, minimum 1
 */
export function calculateTripDuration(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 0;

  try {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const days = differenceInDays(end, start);
    return Math.max(days, 1);
  } catch {
    return 0;
  }
}

/**
 * Builds insurance-specific GTM data from cart item or form data
 * @param item - Cart item for travel insurance (optional)
 * @param formData - Form data from travel insurance store (optional)
 * @returns Partial GaDataLayer with insurance fields
 */
export function buildInsuranceGtmData(
  item?: CartItem | SearchProducts | null,
  formData?: TravelInsuranceForm | null
): Partial<GaDataLayer> {
  const insuranceData: Partial<GaDataLayer> = {};

  let tripStartDate = '';
  let tripEndDate = '';
  let numberOfTraveller = 1;
  let subOptions: Array<{
    id: string;
    details?: { name?: string };
    selected?: boolean;
    totals?: {
      quantity?: number,
      total?: number
    }
  }> = [];

  //extract from item if available
  if (item) {
    if ('inventory_option' in item && item.inventory_option?.sub_options) {
      subOptions = item.inventory_option.sub_options;
    }

     //order criteria in purchase product
    if ('order_criteria' in item && item.order_criteria?.travel_insurance) {
      const criteria = item.order_criteria.travel_insurance;
      tripStartDate = criteria.date1 || '';
      tripEndDate = criteria.date2 || '';
      numberOfTraveller = 1 + (criteria.additional_travellers?.length || 0);
    //search criteria in cart item
    } else if ('search_criteria' in item && item.search_criteria?.travel_insurance) {
      const criteria = item.search_criteria.travel_insurance;
      tripStartDate = criteria.date1 || '';
      tripEndDate = criteria.date2 || '';
      numberOfTraveller = 1 + (criteria.additional_travellers?.length || 0);
    //search product structure
    } else if ('productHeader' in item && item.productHeader?.product_code === 'travel_insurance') {
      tripStartDate = item.entry_date || '';
      tripEndDate = item.exit_date || '';
      subOptions = item.productOptions?.[0]?.sub_options || item.inventory_option?.sub_options || [];
    }
  }

  //override with form data if available
  if (formData) {
    if (formData.travelDates?.startDate) {
      tripStartDate = normaliseDateToString(formData.travelDates.startDate);
    }
    if (formData.travelDates?.endDate) {
      tripEndDate = normaliseDateToString(formData.travelDates.endDate);
    }
    if (formData.travellers?.length) {
      numberOfTraveller = formData.travellers.length;
    }
  }

  tripStartDate = normaliseDateToString(tripStartDate);
  tripEndDate = normaliseDateToString(tripEndDate);

  if (tripStartDate) {
    insuranceData.tripStartDate = tripStartDate;
    insuranceData.daysAheadOfBooking = differenceInDays(parseISO(tripStartDate), startOfToday());
  }

  if (tripEndDate) {
    insuranceData.tripEndDate = tripEndDate;
  }

  if (tripStartDate && tripEndDate) {
    insuranceData.tripDuration = calculateTripDuration(tripStartDate, tripEndDate);
  }

  insuranceData.numberOfTraveller = numberOfTraveller;
  insuranceData.InsuranceAddOn = getInsuranceAddOnNames(subOptions);

  return insuranceData;
}

/**
 * Builds complete GTM data for standalone insurance products
 * doesn't go through gaDataLayerCommon to avoid parking/lounge field pollution.
 * for cross-sell use gaDataLayerCommon + buildInsuranceGtmData instead.
 * @param item - Cart item or search product
 * @param formData - Travel insurance form data
 * @param extend - Additional fields to merge
 * @returns Complete GaDataLayer object for insurance
 */
export function buildInsuranceGaDataLayer(
  item?: CartItem | SearchProducts | null,
  formData?: TravelInsuranceForm | null,
  extend: Partial<GaDataLayer> = {}
): Partial<GaDataLayer> {
  const insuranceSpecificData = buildInsuranceGtmData(item, formData);

  const country = getAppVariable('pois')?.find(
    (poi: { code: string; country?: { name?: string } }) =>
      poi.code === getAppVariable('poi.code')
  )?.country?.name;

  const baseData: Partial<GaDataLayer> = {
    airportCode: getAppVariable('poi.code'),
    airportName: getAppVariable('poi.name'),
    partnerName: getAppVariable('partner_name'),
    siteLanguage: getAppVariable('default_language'),
    serviceType: getAppVariable('primary_product_type'),
    region: getRegionVariable('code'),
    country,
    loginStatus: false,
    userId: undefined,
    event: 'globalParameters',
    ...insuranceSpecificData,
    ...extend,
  };

  return baseData;
}

/**
 * Builds standalone insurance GTM global parameters from a cart item's search_criteria.
 * used on payment page where we don't have the TravelInsuranceForm but have cart data.
 * @param cartItem - cart item with search_criteria.travel_insurance
 * @returns partial GaDataLayer for standalone insurance
 */
export function buildStandaloneInsuranceCartGtmData(
  cartItem: CartItem,
  authData: { isAuthenticated: boolean; userId?: string }
): Partial<GaDataLayer> {
  const insuranceGtmData = buildInsuranceGtmData(cartItem);

  const country = getAppVariable('pois')?.find(
    (poi: { code: string; country?: { name?: string } }) =>
      poi.code === getAppVariable('poi.code')
  )?.country?.name;

  return {
    airportCode: getAppVariable('poi.code'),
    airportName: getAppVariable('poi.name'),
    partnerName: getAppVariable('partner_name'),
    siteLanguage: getAppVariable('default_language'),
    serviceType: getAppVariable('primary_product_type'),
    region: getRegionVariable('code'),
    country,
    loginStatus: authData.isAuthenticated,
    userId: authData.userId,
    event: 'globalParameters',
    ...insuranceGtmData,
  };
}
